import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { departments, employees, orgSettings, users } from '@/lib/api'

export const orgKeys = {
  departments: ['org', 'departments'] as const,
  settings: ['org', 'settings'] as const,
  userOptions: (q: string, limit: number) => ['org', 'user-options', q, limit] as const,
}

export function useDepartments() {
  return useQuery({
    queryKey: orgKeys.departments,
    queryFn: async () => (await departments.list()).data,
  })
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      id: number
      payload: { head_user_id?: number | null; is_active?: boolean }
    }) => (await departments.update(vars.id, vars.payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orgKeys.departments }),
  })
}

export function useOrgSettings(enabled = true) {
  return useQuery({
    queryKey: orgKeys.settings,
    queryFn: async () => (await orgSettings.get()).data,
    enabled,
  })
}

export function useUpdateOrgSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { coo_user_id?: number | null; ceo_user_id?: number | null }) =>
      (await orgSettings.update(payload)).data,
    onSuccess: (data) => queryClient.setQueryData(orgKeys.settings, data),
  })
}

export function useUserOptions(q = '', limit = 200, enabled = true) {
  return useQuery({
    queryKey: orgKeys.userOptions(q, limit),
    queryFn: async () => (await users.options({ q: q || undefined, limit })).data,
    enabled,
    staleTime: 60_000,
  })
}

export function useUpdateEmployee(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { is_managerial: boolean }) =>
      (await employees.update(id, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['employees'] }),
  })
}
